import React, { useRef, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { select as D3select } from 'd3';
import { toast } from 'react-toastify';

import { makeStyles, useTheme } from '@mui/styles';

import D3Heatmap from '../../d3/D3Heatmap';

import OSAnomalyService from '../../data/os-anomaly-service';
// import anomalyHeatmap from '../../data/anomaly-heatmap';

const useStyles = makeStyles(theme => ({
    root: {

    },
    heatmap: {
        '& rect': {
            transition: 'transform 0.3s ease-in-out',
            opacity: 0.2,
            '&.hover': {
                transform: 'scale(2)',
                opacity: 0.8,
            },
        },
    },
}));

const CRITICAL = { value: 1, label: 'Critical' };
const HIGH = { value: 2, label: 'High' };
const MEDIUM = { value: 3, label: 'Medium' };
const LOW = { value: 4, label: 'Low' };
const series = [CRITICAL, HIGH, MEDIUM, LOW];

const Heatmap = (props) => {
    const d3Container = useRef(null);
    const classes = useStyles();
    const theme = useTheme();
    const [data, setData] = React.useState([]);
    const [start, setStart] = React.useState();
    const [end, setEnd] = React.useState();
    // setInterval is already taken
    const [intvl, setIntvl] = React.useState(3 * 60); // 3 hours
    const [init, setInit] = React.useState(false);

    const expandData = (osData) => {
        return osData.reduce((a, c) => {
            c.anomaly_ranges.buckets.forEach(x => {
                const dataPoint = {
                    timestamp: new Date(c.key_as_string),
                    value: x.doc_count
                };
                switch (x.key) {
                    case 'low':
                        dataPoint.series = 4;
                        break;
                    case 'medium':
                        dataPoint.series = 3;
                        break;
                    case 'high':
                        dataPoint.series = 2;
                        break;
                    case 'critical':
                        dataPoint.series = 1;
                        break;
                }
                a.push(dataPoint);
            });
            return a;
        }, []).filter(x => x.value);
    };

    const handleHeatmapClick = (e, d) => {
        props.clickHandler(e, d);
    };

    useEffect(() => {
        if (d3Container.current && init) {
            const node = d3Container.current;
            D3select(node).datum(data)
                .call(D3Heatmap()
                    .height(props.height)
                    .width(props.width)
                    .start(start)
                    .end(end)
                    .interval(intvl)
                    .series(series)
                    .theme(theme))
                    .on('click', handleHeatmapClick);
        }
    }, [props.width, props.height, props.severity, theme, start, end, intvl, data, init]);

    useEffect(() => {
        // create some initial data
        let start = new Date(props.start);
        // if end is not provided, use now
        let end = new Date(props.end);
        if (isNaN(start.getTime())) {
            start = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000); // default to 14 days ago
        }
        if (isNaN(end.getTime())) {
            end = new Date(); // default to now
        }

        // function for nice round intervals depending on the time range
        const getInterval = (start, end) => {
            const diff = end.getTime() - start.getTime();
            if (diff < 60 * 60 * 1000) { // less than an hour
                return 1; // 1 minutes
            } else if (diff < 12 * 60 * 60 * 1000) { // less than 12 hours
                return 1;
            } else if (diff < 24 * 60 * 60 * 1000) { // less than a day
                return 10;
            } else if (diff < 7 * 24 * 60 * 60 * 1000) { // less than a week
                return 15;
            } else if (diff < 14 * 24 * 60 * 60 * 1000) { // less than 2 weeks
                return 2 * 60;
            } else if (diff < 30 * 24 * 60 * 60 * 1000) { // less than a month
                return 4 * 60;
            } else if (diff < 90 * 24 * 60 * 60 * 1000) { // less than 2 months
                return 12 * 60;
            } else if (diff < 180 * 24 * 60 * 60 * 1000) { // less than 6 months
                return 24 * 60;
            } else if (diff < 365 * 24 * 60 * 60 * 1000) { // less than a year
                return 2 * 24 * 60;
            } else {
                return 7 * 24 * 60; // default to weekly
            }
        };
        const interval = getInterval(start, end);
        setIntvl(interval);
        setStart(start);
        setEnd(end);
        setInit(true);
    }, [props.start, props.end]);

    useEffect(() => {
        if (!init) return;

        if (start && end && intvl) {
            // const r = expandData(anomalyHeatmap);
            // console.log(r);
            // setData(r);
            if (props.ruleID.includes('all')) {
                OSAnomalyService.getHeatmap(intvl + 'm', new Date(start).valueOf(), new Date(end).valueOf()).then(res => {
                    if (res && res.length) {
                        const r = expandData(res);
                        setData(r);
                    }
                }, err => {
                    console.error("Unable to get heatmap data ::: ", err);
                    toast.error('Unable to get heatmap data');
                });
            } else {
                OSAnomalyService.getHeatmapByRule(props.ruleID, intvl + 'm', new Date(start).valueOf(), new Date(end).valueOf()).then(res => {
                    if (res && res.length) {
                        const r = expandData(res);
                        setData(r);
                    }
                }, err => {
                    console.error('Unable to get heatmap data for rule: ', props.ruleID, ' ::: ', err);
                    toast.error('Unable to get heatmap data for rule: ' + props.ruleID);
                });
            }
        }

        // if (fakeDataRunning.current) clearInterval(fakeDataRunning.current); // we only need to run this once
        // // fakeDataRunning.current = true;

        // const generateTimestamp = () => {
        //     // generate a random timestamp in the past 7 days
        //     const randomMinutes = Math.floor(Math.random() * 10080);
        //     return new Date(end.getTime() - randomMinutes * 60000);
        // };

        // // generate a random series name for the ['critical', 'high', 'medium', 'low']
        // const generateSeries = () => {
        //     return series[Math.floor(Math.random() * series.length)].value;
        // };

        // // generate a random value between 1 and 100
        // const generateValue = () => {
        //     return Math.floor(Math.random() * 100) + 1; // random value between 1 and 100
        // };

        // // generate initial data, one for every 10 minutes in the past 7 days
        // const initialData = [];
        // // round to nearest interval
        // let d = new Date(Math.round(start.getTime() / (intvl * 60 * 1000)) * (intvl * 60 * 1000));
        // while (d.getTime() < end.getTime()) {
        //     initialData.push({
        //         timestamp: d,
        //         series: generateSeries(),
        //         value: generateValue(),

        //     });
        //     // increment by the interval in minutes
        //     d = new Date(d.getTime() + intvl * 60 * 1000);
        // }
        // console.log('INITIAL DATA ::: ', initialData);
        // setData(initialData);

        // fakeDataRunning.current = setInterval(() => {
        //     const newData = {
        //         timestamp: generateTimestamp(),
        //         series: generateSeries(),
        //         value: generateValue(),
        //     };
        //     setData(current => [
        //         ...current,
        //         newData,
        //     ]);
        // }, 11000); // every 11 seconds
    }, [start, end, init, intvl, props.ruleID]);

    return (
        <div ref={d3Container} className={clsx('heatmap', classes.root)}>
        </div>
    );
};

Heatmap.propTypes = {
    height: PropTypes.number,
    width: PropTypes.number,
    data: PropTypes.array,
    start: PropTypes.number,
    end: PropTypes.number,
    ruleID: PropTypes.string,
    clickHandler: PropTypes.func,
    severity: PropTypes.number,
};

export {
    CRITICAL,
    HIGH,
    MEDIUM,
    LOW,
    Heatmap as default,
};
